import type { FC, PropsWithChildren } from 'react';
import { useEffect } from 'react';
import debounce from 'lodash/debounce';
import Navbar from './navbar';
import Footer from './footer';
import { useIsFramed } from '../use-is-framed';

/**
 * The configurator mutates the DOM on every stepper press and every summary
 * recalculation, so the height post is debounced: a burst of changes settles
 * into a single message to the parent frame instead of one per mutation.
 */
const HEIGHT_SYNC_DEBOUNCE_MS = 120;

type PricingPageWrapperProps = PropsWithChildren<{ hideChrome?: boolean }>;

const PricingPageWrapper: FC<PricingPageWrapperProps> = ({
  children,
  hideChrome = false,
}) => {
  const isFramed = useIsFramed();

  useEffect(() => {
    if (!isFramed) return;

    const postHeight = () => {
      window.parent.postMessage(
        { type: 'setHeight', height: document.body.scrollHeight },
        '*'
      );
    };
    const sendHeight = debounce(postHeight, HEIGHT_SYNC_DEBOUNCE_MS);

    // The first post is immediate so the frame is sized before the visitor
    // sees it; everything after it is debounced.
    postHeight();
    window.addEventListener('resize', sendHeight);

    const observer = new MutationObserver(sendHeight);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      sendHeight.cancel();
      window.removeEventListener('resize', sendHeight);
      observer.disconnect();
    };
  }, [isFramed]);

  return (
    <div
      className={`h-dvh overflow-hidden overflow-y-auto ${hideChrome ? 'pt-8' : ''}`}
    >
      {!isFramed && !hideChrome ? <Navbar /> : null}
      {children}
      {!isFramed && !hideChrome ? <Footer /> : null}
    </div>
  );
};

export default PricingPageWrapper;
